/**
 * A cassette's band items, held and played into the flat `sent | mark`
 * stream the renderer regroups (`streamToSegments`), TT-1273.
 *
 * The band read (platform `domain/spoken/band_read.py`) links its items so
 * a client never searches or folds: every item has an `id`, the same in
 * every read; a mark sits `on` its sentence (anchored) or `follows` it
 * (free); a revision names the item it `revises`. A follower pages the cold
 * read once, then asks `since` its last seq; a delta may repeat items it
 * holds, and holding by `id` makes a repeat replace itself.
 *
 * The one rule played here is the reveal (spec, Play): an item shows once
 * its `known` has passed T (`knownBy`), and of an item and its revisions
 * the `version` the newest revealed revision carries stands for it
 * (`standing`).
 */

/**
 * Hold `items` on top of `held` (a Map by id, never mutated). Returns the
 * new Map, or `held` itself when nothing new came, so a caller can skip a
 * re-render. The uncut tail (`limbo`) is not held.
 */
export function holdItems(held, items) {
  let next = null;
  for (const item of items || []) {
    if (!item?.id || item.limbo) continue;
    const had = (next || held).get(item.id);
    if (had && JSON.stringify(had) === JSON.stringify(item)) continue;
    if (!next) next = new Map(held);
    next.set(item.id, item);
  }
  return next || held;
}

/**
 * The reveal rule, as known: only the items the cassette had written by
 * source time `t`. An item written before stamps carries no `known` and
 * always shows; `t` null is the live edge (everything held).
 */
export function knownBy(items, t) {
  if (t == null) return items;
  return items.filter((item) => item.known == null || item.known <= t);
}

const byKnown = (a, b) => (a.known ?? -Infinity) - (b.known ?? -Infinity) || (a.seq ?? 0) - (b.seq ?? 0);

// What a revision shows: its `version`, the record as it stands after that
// revision (the platform folds it), in the place of the item it revises,
// with the revision's own stamps. A revision without one (a read older
// than versions) shows the item as it was.
function shown(item, revision) {
  if (!revision.version || typeof revision.version !== "object") return item;
  const place = { kind: item.kind, id: item.id, idx: item.idx, on: item.on, follows: item.follows };
  return { annotator: item.annotator, ...revision.version, ...place, about: revision.about, known: revision.known };
}

/**
 * What stands for each item: itself, or the `version` the newest of its
 * revisions among `items` (those already revealed) carries. A revision is
 * a version, not an item of its own; nothing is folded here.
 */
export function standing(items) {
  const revisions = new Map();
  for (const item of items) {
    if (item.revises) revisions.set(item.revises, [...(revisions.get(item.revises) || []), item]);
  }
  return items
    .filter((item) => !item.revises)
    .map((item) => {
      const mine = revisions.get(item.id);
      return mine ? shown(item, [...mine].sort(byKnown).at(-1)) : item;
    });
}

// A mark as its sentence carries it: the annotator and its own fields.
function carried({ kind, anchored, on, ...rest }) {
  return rest;
}

const bySeq = (a, b) => (a.seq ?? 0) - (b.seq ?? 0);

/**
 * The standing items as the flat stream: sentences in `idx` order, each
 * carrying the marks `on` it and followed by the marks that follow it. A
 * mark whose sentence is not held (yet) leads.
 */
export function playStream(items) {
  const sents = [];
  const on = new Map();
  const follows = new Map();
  const add = (map, key, item) => map.set(key, [...(map.get(key) || []), item]);
  for (const item of standing(items)) {
    if (item.kind === "sent") sents.push(item);
    else if (item.on) add(on, item.on, item);
    else if (item.follows) add(follows, item.follows, item);
  }
  sents.sort((a, b) => a.idx - b.idx);
  const heldIds = new Set(sents.map((s) => s.id));
  const out = [];
  for (const [id, marks] of [...on, ...follows]) {
    if (!heldIds.has(id)) out.push(...marks.sort(bySeq));
  }
  for (const sent of sents) {
    const marks = (on.get(sent.id) || []).sort(bySeq).map(carried);
    out.push(marks.length ? { ...sent, marks } : sent);
    out.push(...(follows.get(sent.id) || []).sort(bySeq));
  }
  return out;
}
